import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, CaretDown, BookOpen, ChatCircle, ShieldCheck, SignOut, Devices, Phone, Coins, CreditCard, Wrench, Question, UserPlus, ChartBar, Folders, Monitor, SpeakerHigh, SpeakerSlash, ListChecks, MapPin, Package, Factory, Receipt, SignIn, EnvelopeSimple } from "@phosphor-icons/react";
import api, { fetchInventoryMe } from "@/lib/api";
import LanguagePicker from "@/components/LanguagePicker";
import { useT } from "@/lib/i18n";
import { useUnreadBadge } from "@/lib/conv/notificationStore";
import { isSoundEnabled, setSoundEnabled, previewSound } from "@/lib/conv/sounds";

/**
 * AccountMenu — top-right dropdown for the signed-in user.
 *
 * Replaces the old standalone "Logbook" header button by tucking lower-frequency
 * navigation (Logbook, Conversations, Sign-out-of-all-devices) behind a single
 * affordance attached to the email/name pill. Always visible; entries auto-hide
 * for guests / customers / non-admins as appropriate.
 *
 * Usage:
 *   <AccountMenu user={user} onLogout={handleLogout} onLogoutAll={handleLogoutAll} />
 */
export default function AccountMenu({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [canSvcTech, setCanSvcTech] = useState(false);
  // iter150AZ.91 — inventory module gating + sales-only menu mode.
  // ``inv.has_access`` controls visibility of the "Inventory" menu
  // entry; ``inv.sales_only`` (non-super-admins only) hides every
  // other admin/ops/help entry so a sales rep sees a slimmed-down menu.
  const [inv, setInv] = useState({ has_access: false, sales_only: false });
  // iter150AZ.130 — suppliers module gating
  const [sup, setSup] = useState({ has_view: false, has_edit: false });
  const [soundOn, setSoundOnState] = useState(() => isSoundEnabled());
  const wrapRef = useRef(null);
  const { totalUnread, mentionUnread } = useUnreadBadge();

  useEffect(() => {
    const onToggle = (e) => setSoundOnState(!!e.detail?.enabled);
    window.addEventListener("conv:sound_toggled", onToggle);
    return () => window.removeEventListener("conv:sound_toggled", onToggle);
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    api.get("/me/can-service-tech")
      .then((r) => setCanSvcTech(!!r.data?.can))
      .catch(() => setCanSvcTech(false));
  }, [user?.email]);

  useEffect(() => {
    // iter150AZ.91 — load inventory access + sales-only mode once per
    // signed-in session. Failures degrade silently to "no access".
    if (!user?.email) return;
    fetchInventoryMe()
      .then((m) => setInv({
        has_access: !!m?.has_access,
        sales_only: !!m?.sales_only,
      }))
      .catch(() => setInv({ has_access: false, sales_only: false }));
  }, [user?.email]);

  useEffect(() => {
    // iter150AZ.130 — surface Suppliers menu only to super-admins or
    // users granted `can_view_suppliers`. Endpoint always returns 200
    // so a 404/401 here means the route isn't deployed yet.
    if (!user?.email) return;
    api.get("/suppliers/me")
      .then((r) => setSup({
        has_view: !!r.data?.has_view,
        has_edit: !!r.data?.has_edit,
      }))
      .catch(() => setSup({ has_view: false, has_edit: false }));
  }, [user?.email]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user?.email) return null;
  const isGuest = String(user.email).endsWith("@local.guest");
  const isCustomer = !!user?.is_customer;
  const isAdmin = !!user?.is_admin;
  const isSuper = !!user?.is_super_admin;
  const businessDomain = /@(usaircompressor|aircompressorcanada|aircompressor)\.com$/i.test(user.email);
  const isStaff = isAdmin || businessDomain;

  const goto = (path) => () => { setOpen(false); navigate(path); };

  return (
    <div ref={wrapRef} className="relative flex items-center gap-1.5">
      <LanguagePicker />
      <button
        type="button"
        data-testid="account-menu-button"
        onClick={() => setOpen((x) => !x)}
        className="relative h-8 px-2 inline-flex items-center gap-1.5 rounded-sm border border-slate-200 hover:bg-slate-100 text-xs text-slate-700 transition-colors"
        title={user.email}
      >
        <User size={14} weight="bold" className="text-slate-500" />
        <span className="hidden sm:inline max-w-[150px] truncate">{user.name || user.email}</span>
        {isAdmin && (
          <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded-sm">
            Admin
          </span>
        )}
        <CaretDown size={11} weight="bold" className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        {/* iter137.1 — Both states are RED to be unmissable. Differentiation:
              • mention   = larger (3×3) breathing red dot with pulsing halo.
              • unread    = smaller (2×2) solid red dot, no animation. */}
        {mentionUnread > 0 && (
          <span
            data-testid="account-menu-mention-dot"
            aria-label={`${mentionUnread} unread mention${mentionUnread === 1 ? "" : "s"}`}
            className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-500 ring-2 ring-white animate-conv-breathe"
          />
        )}
        {mentionUnread === 0 && totalUnread > 0 && (
          <span
            data-testid="account-menu-unread-dot"
            aria-label={`${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`}
            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"
          />
        )}
      </button>

      {open && (
        <div
          data-testid="account-menu-panel"
          /* iter150f — mobile fix: long menu was clipped because the panel
             used overflow-hidden. Allow vertical scroll and cap height to
             the viewport so users can reach every item on phones. */
          className="absolute right-0 top-full mt-1.5 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-[200] overflow-y-auto overscroll-contain max-h-[calc(100dvh-72px)] sm:max-h-[80vh]"
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Signed in as</div>
            <div className="text-sm font-bold text-slate-900 truncate">{user.name || user.email}</div>
            <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
          </div>

          <div className="py-1">
            {/* iter150AZ.91 — Inventory module. Surfaces at the top of
                the menu when the user holds ``inventory.access`` (or
                is a super-admin). Hidden from sales-only users so
                their menu stays focused on sales work. */}
            {inv.has_access && !inv.sales_only && (
              <MenuItem icon={Package} label="Inventory"
                desc="Parts, warehouses, transfers, unassigned products"
                onClick={goto("/admin/inventory")} testId="account-menu-inventory" />
            )}
            {/* iter150AZ.130 — Suppliers directory. Gated by
                ``can_view_suppliers`` or super-admin. */}
            {sup.has_view && !inv.sales_only && (
              <MenuItem icon={Factory} label={t("menu.suppliers", "Suppliers")}
                desc={t("menu.suppliers_desc", "Vendor directory · POs · contacts · notes")}
                onClick={goto("/admin/suppliers")} testId="account-menu-suppliers" />
            )}
            {/* iter171 — Supplier Invoices (Vendor Shipments). Same
                gate as Suppliers. */}
            {sup.has_view && !inv.sales_only && (
              <MenuItem icon={Receipt} label={t("menu.supplier_invoices", "Supplier invoices")}
                desc={t("menu.supplier_invoices_desc", "Vendor shipments · scanned invoices · linked POs")}
                onClick={goto("/admin/supplier-invoices")} testId="account-menu-supplier-invoices" />
            )}
            {isStaff && !inv.sales_only && (
              <MenuItem icon={ChatCircle} label={t("menu.conversations")} desc={t("menu.conversations_desc")}
                onClick={goto("/conversations-v2")} testId="account-menu-conversations"
                rightAdornment={
                  mentionUnread > 0 ? (
                    <span
                      data-testid="account-menu-conversations-mention-badge"
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-rose-500 text-white animate-conv-breathe"
                    >
                      {mentionUnread > 99 ? "99+" : mentionUnread}
                    </span>
                  ) : totalUnread > 0 ? (
                    <span
                      data-testid="account-menu-conversations-unread-badge"
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-rose-500 text-white"
                    >
                      {totalUnread > 99 ? "99+" : totalUnread}
                    </span>
                  ) : null
                }
              />
            )}
            {isStaff && !inv.sales_only && (
              <MenuItem icon={BookOpen} label={t("menu.logbook")} desc={t("menu.logbook_desc")}
                onClick={goto("/logbook")} testId="account-menu-logbook" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={ShieldCheck} label={t("menu.hot_leads")} desc={t("menu.hot_leads_desc")}
                onClick={goto("/admin/hot-leads")} testId="account-menu-hotleads" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={Phone} label={t("menu.twilio_credentials")} desc={t("menu.twilio_credentials_desc")}
                onClick={goto("/admin/twilio-credentials")} testId="account-menu-twilio-creds" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={Phone} label={t("menu.call_routing")} desc={t("menu.call_routing_desc")}
                onClick={goto("/admin/call-routing")} testId="account-menu-call-routing" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={Coins} label={t("menu.aging_report")} desc={t("menu.aging_report_desc")}
                onClick={goto("/admin/aging")} testId="account-menu-aging" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={CreditCard} label={t("menu.payment_credentials")} desc={t("menu.payment_credentials_desc")}
                onClick={goto("/admin/payment-credentials")} testId="account-menu-payment-creds" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={Monitor} label="Screen Share / SFU" desc="LiveKit credentials for screen sharing"
                onClick={goto("/admin/sfu-credentials")} testId="account-menu-sfu-creds" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={UserPlus} label={t("onboard.menu_label")} desc={t("onboard.menu_desc")}
                onClick={goto("/admin/onboard")} testId="account-menu-onboard" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={ChartBar} label={t("menu.manual_analytics")} desc={t("menu.manual_analytics_desc")}
                onClick={goto("/admin/manual-stats")} testId="account-menu-manual-stats" />
            )}
            {isSuper && !inv.sales_only && (
              <MenuItem icon={MapPin} label="Field tools · Settings"
                desc="Google Maps key for nearby-customer search"
                onClick={goto("/admin/field-tools")} testId="account-menu-field-tools" />
            )}
            {canSvcTech && !inv.sales_only && (
              <MenuItem icon={Wrench} label={t("service_tech.short_label")} desc={t("service_tech.menu_desc")}
                onClick={goto("/service-tech")} testId="account-menu-svc-tech" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={SignIn} label={t("menu.login_activity", "Login activity")}
                desc={t("menu.login_activity_desc", "Who logged in, when, from where")}
                onClick={goto("/admin/login-activity")} testId="account-menu-login-activity" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={Devices} label={t("menu.active_sessions", "Active sessions")}
                desc={t("menu.active_sessions_desc", "Signed-in users right now · revoke")}
                onClick={goto("/admin/sessions")} testId="account-menu-active-sessions" />
            )}
            {isAdmin && !inv.sales_only && (
              <MenuItem icon={EnvelopeSimple} label={t("menu.customer_invites", "Customer invites")}
                desc={t("menu.customer_invites_desc", "Bulk-invite customers to sign in")}
                onClick={goto("/admin/customer-invites")} testId="account-menu-customer-invites" />
            )}
            {isCustomer && (
              <MenuItem icon={User} label={t("menu.my_account")} desc={t("menu.my_account_desc")}
                onClick={goto("/my-account")} testId="account-menu-myaccount" />
            )}
            {/* iter203b — every signed-in user can view their own history */}
            <MenuItem icon={SignIn} label={t("menu.my_login_history", "My login history")}
              desc={t("menu.my_login_history_desc", "Your recent sign-ins · sign out everywhere")}
              onClick={goto("/account/login-history")} testId="account-menu-login-history" />
            <MenuItem icon={Folders} label={t("menu.public_files")} desc={t("menu.public_files_desc")}
              onClick={goto("/public-files")} testId="account-menu-public-files" />
            <MenuItem icon={ListChecks}
              label={t("my_requests.nav_label", "My requested changes")}
              desc={t("my_requests.nav_tooltip", "See status of every request you submitted")}
              onClick={goto("/my-requests")} testId="account-menu-my-requests" />
            <MenuItem icon={Question} label={t("menu.help_manual")} desc={t("menu.help_manual_desc")}
              onClick={goto("/help")} testId="account-menu-help" />
          </div>

          <div className="border-t border-slate-100 py-1">
            <MenuItem
              icon={soundOn ? SpeakerHigh : SpeakerSlash}
              label={soundOn ? t("notif.sound_on", "Notification sounds: On") : t("notif.sound_off", "Notification sounds: Off")}
              desc={t("notif.sound_desc", "Plays a soft tick on new messages, a chime on @mentions")}
              onClick={() => {
                const next = !soundOn;
                setSoundEnabled(next);
                setSoundOnState(next);
                // Play a quick preview when toggling ON so the user
                // hears the new clip immediately. (Browsers require
                // a user gesture to play audio — the click counts.)
                if (next) {
                  try { previewSound("tick"); } catch { /* noop */ }
                }
              }}
              testId="account-menu-sound-toggle"
            />
            <MenuItem icon={SignOut} label={t("nav.signout")} tone="rose"
              onClick={() => { setOpen(false); onLogout?.(); }} testId="account-menu-logout" />
            {!isGuest && onLogoutAll && (
              <MenuItem icon={Devices} label={t("menu.signout_all")} tone="rose"
                desc={t("menu.signout_all_desc")}
                onClick={() => {
                  setOpen(false);
                  if (window.confirm("Sign out of all devices? This will revoke every active session tied to this email.")) {
                    onLogoutAll();
                  }
                }}
                testId="account-menu-logout-all" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, desc, onClick, testId, tone = "slate", rightAdornment = null }) {
  const toneCls = tone === "rose"
    ? "text-rose-700 hover:bg-rose-50 [&_svg]:text-rose-500"
    : "text-slate-700 hover:bg-slate-50 [&_svg]:text-slate-500";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 flex items-start gap-2.5 text-sm transition-colors ${toneCls}`}
    >
      <Icon size={16} weight="bold" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold leading-tight flex items-center gap-2">
          <span className="truncate">{label}</span>
          {rightAdornment && <span className="ml-auto shrink-0">{rightAdornment}</span>}
        </div>
        {desc && <div className="text-[11px] text-slate-400 leading-tight mt-0.5">{desc}</div>}
      </div>
    </button>
  );
}
